<script setup lang="ts">
import { XIcon, XTooltip } from '@clicksign/excalibur';
import {
  useDebounceFn,
  useEventListener,
  useIntersectionObserver,
  useStorage,
  whenever,
} from '@vueuse/core';
import { useI18n } from '#i18n';

import { RubricateArtifactSchema } from '~~requirement';

import { calculateMaxCoordinatePosition, coordinateBasedOnOffsetAndPosition, getPropertyScaleFactor } from '../../utils/position-sign-utils';
import StampWrapper from './StampWrapper/StampWrapper.vue';
import { Rubricate } from '../../schemas';
import { PagesWithRubricates } from '../../composables/use-rubricate';

type CalculatedCoordinates = {
  x?: number;
  y?: number;
};

export type SignatureStampProps = {
  wrapperPositionSign?: HTMLElement;
  areRubricatesReady: boolean;
  rubricates?: Rubricate;
  initialSignatureStamps: (PagesWithRubricates & CalculatedCoordinates)[];
  artefact?: RubricateArtifactSchema;
  isLoading: boolean;
  isError: boolean;
  documentIndex: number,
};

export type SignatureStampPublicAPI = {
  nextStamp: () => void;
  calculateCoordinatesFromEachPositionStamp: () => void;
};

export type SignatureStampEmits = {
  (e: 'total-signed', totalSigned: number): void;
  (e: 'signature-click'): void;
  (e: 'signatures-total', totalSignatures: number): void;
};

const props = defineProps<SignatureStampProps>();
const emit = defineEmits<SignatureStampEmits>();

const { t } = useI18n();
const gtm = useGtm();

const signatureContainer = ref<HTMLElement>();
const signatureElements = ref<HTMLElement[]>([]);
const currentSignaturesPages = ref(props.initialSignatureStamps);
const stampSelectedIndex = ref(-1);

const isIntersectingFirst = ref(false);

const thereIsSignedSignature = ref(false);

const stampSignedIndexList = ref(new Set<number>([]));
const isTooltipDismissed = useStorage('canEnableStampPositionSignTooltip', false);

const currentStampIndex = ref(0);

const isPageSigned = (index: number) => !!props.artefact || stampSignedIndexList.value.has(index);

const canShowTooltip = computed(() => !isTooltipDismissed.value && !thereIsSignedSignature.value);

const tooltipProps = computed(() => ({
  advanced: true,
  placement: 'bottom',
  isOpen: canShowTooltip.value && isIntersectingFirst.value,
  disabled: !canShowTooltip.value,
  closeButton: true,
  openByHover: false,
  title: t('document.rubricate.signatureStamp.tooltipTitle'),
}));

const scale = ref(getPropertyScaleFactor());

function calculateCoordinatesFromEachPositionStamp() {
  scale.value = getPropertyScaleFactor();

  currentSignaturesPages.value = currentSignaturesPages.value.map((page, index) => {
    const signatureElement = signatureElements.value[index];

    const pageElement = props.wrapperPositionSign?.querySelector<HTMLDivElement>(
      `[data-page-number="${page.page}"]`,
    );

    if (!pageElement || !signatureElement) return page;

    const [x, y] = coordinateBasedOnOffsetAndPosition(pageElement, page.position);

    if (x === undefined || y === undefined) return page;

    const [finalX, finalY] = calculateMaxCoordinatePosition(
      [x, y],
      pageElement,
      signatureElement,
    );

    return { ...page, x: finalX, y: finalY };
  });
}

function sortSignatures() {
  currentSignaturesPages.value = currentSignaturesPages.value.toSorted(
    ({ x: firstSignX = 0, y: firstSignY = 0 }, { x: secondSignX = 0, y: secondSignY = 0 }) => {
      const firstTotalDistance = firstSignX + firstSignY;
      const secondTotalDistance = secondSignX + secondSignY;

      return firstTotalDistance - secondTotalDistance;
    },
  );
}

function fillPagesWithDefaultPositions() {
  const firstStamp = currentSignaturesPages?.value.at(0);

  if (!firstStamp?.fromApi || !firstStamp?.kind.length) return;

  const pdfPageCount = props.wrapperPositionSign?.querySelectorAll<HTMLDivElement>(
    '[data-page-number]',
  ).length;

  if (!pdfPageCount) return;

  if (firstStamp.page === 'all') {
    currentSignaturesPages.value = Array.from({ length: Number(pdfPageCount) }, (_, index) => ({
      page: index + 1,
      position: [1, 1],
      kind: firstStamp.kind,
      pageOffset: [0, 0],
    }));
  } else {
    const pages = [...new Set(String(firstStamp.page).split(','))];

    pages.length = Math.min(pages.length, pdfPageCount);

    currentSignaturesPages.value = pages.map((page) => ({
      page,
      position: [1, 1],
      kind: firstStamp.kind,
      pageOffset: [0, 0],
    }));
  }
}

let canFocusButton = true;

const { stop } = useIntersectionObserver(() => signatureElements.value[0], async (entries: IntersectionObserverEntry[]) => {
  if (props.artefact) return stop();

  const firstEntry = entries[0];
  const isIntersecting = firstEntry?.isIntersecting;

  isIntersectingFirst.value = !!isIntersecting;

  await nextTick();

  if (isIntersecting && canFocusButton) {
    stampSelectedIndex.value = 0;

    if (firstEntry.target instanceof HTMLElement) {
      firstEntry?.target?.focus();
      canFocusButton = false;
    }
  }
});

const SIGNATURE_CACHE_PREFIX = 'signature-index-cache';
const signaturesIndexCache = useState<number[] | null>(`${SIGNATURE_CACHE_PREFIX}-${props.documentIndex}`);
const TOTAL_SIGNED_EVENT = 'total-signed';

function signStamp() {
  thereIsSignedSignature.value = true;
  stampSignedIndexList.value.add(currentStampIndex.value);
  currentSignaturesPages.value = [...currentSignaturesPages.value];
  signaturesIndexCache.value = [...stampSignedIndexList.value];
  emit(TOTAL_SIGNED_EVENT, stampSignedIndexList.value.size);
  gtm?.trackEvent({
    event: 'ga-send',
    ga_event_category: 'positioned-sign-signer',
    ga_event_name: 'positioned-sign-step',
  });
}

let hasPendingSignature = false;

function signatureHandler(index: number) {
  if (!!props.artefact || isPageSigned(index)) return;

  isTooltipDismissed.value = true;
  currentStampIndex.value = index;
  hasPendingSignature = true;

  if (props.areRubricatesReady) signStamp();
  else emit('signature-click');
}

function handleKeyDown(e: KeyboardEvent, index: number) {
  if (e.key === 'Enter') {
    e.preventDefault();
    e.stopPropagation();

    signatureHandler(index);
  }
}

function togglePressedClass(e: MouseEvent, styleClass: string) {
  const button = e.currentTarget as HTMLButtonElement;

  button?.classList.toggle(styleClass);
}

function incrementIndex(increment: number, currentIndex: number) {
  const maxIndex = currentSignaturesPages.value.length - 1;
  const futureIndex = Math.max(currentIndex + increment, 0);

  if (futureIndex > maxIndex) return incrementIndex(0, 0);

  if (stampSignedIndexList.value.has(futureIndex)) {
    const newIncrement = increment + 1;

    return incrementIndex(newIncrement, currentIndex);
  }

  return futureIndex;
}

function nextStamp() {
  stampSelectedIndex.value = incrementIndex(1, stampSelectedIndex.value);

  signatureElements.value[stampSelectedIndex.value]?.focus({ preventScroll: true });
  signatureElements.value[stampSelectedIndex.value]?.scrollIntoView({ behavior: 'smooth' });
}

const configureSignatures = async () => {
  await nextTick();

  fillPagesWithDefaultPositions();

  await nextTick();

  emit('signatures-total', currentSignaturesPages.value.length);

  calculateCoordinatesFromEachPositionStamp();
  sortSignatures();

  useEventListener('resize', useDebounceFn(calculateCoordinatesFromEachPositionStamp, 50));
  if (props.artefact) emit(TOTAL_SIGNED_EVENT, currentSignaturesPages.value.length);
};

// TODO: replace it by clearNuxtState after we update nuxt to 3.6.0
// @link https://github.com/nuxt/nuxt/blob/bde696a0db748d6292f7bc01c9b1780563482a11/packages/nuxt/src/app/composables/state.ts#L41
function resetSignaturesCache() {
  const nuxt = useNuxtApp();

  const { state } = nuxt.payload;

  Object.keys(state).forEach((key) => {
    if (key.includes(SIGNATURE_CACHE_PREFIX)) state[key] = undefined;
  });
}

function resetSignatures() {
  currentStampIndex.value = 0;
  stampSignedIndexList.value = new Set();
  emit(TOTAL_SIGNED_EVENT, 0);
  resetSignaturesCache();
}

const configureRubricate = () => {
  if (signaturesIndexCache.value) signaturesIndexCache.value.forEach(signatureHandler);
  if (hasPendingSignature) signStamp();

  hasPendingSignature = false;
};

whenever(() => !props.isLoading, configureSignatures);
whenever(() => !!props.isError, resetSignatures);
whenever(() => !!props.areRubricatesReady, configureRubricate);

onMounted(() => {
  if (!props.isLoading) configureSignatures();
  if (props.isError) resetSignatures();
  if (props.areRubricatesReady) configureRubricate();
});

const artefactProperty = computed(() => ({
  initials: props.artefact?.initials || props.rubricates?.initials,
  manuscript: props.artefact?.manuscript?.imageUrl || props.rubricates?.manuscript,
}));

defineExpose<SignatureStampPublicAPI>({
  nextStamp,
  calculateCoordinatesFromEachPositionStamp,
});

</script>

<template>
  <Teleport to=".pdfViewer">
    <div
      v-show="!isLoading"
      ref="signatureContainer"
      :class="$style.signatures"
    >
      <component
        v-bind="index === 0 ? tooltipProps : {}"
        :is="index === 0 ? XTooltip : StampWrapper"
        v-for="(page, index) in currentSignaturesPages"
        :key="index"
        :class="$style.tooltip"
        :style="{
          top: `${page.y}px`,
          left: `${page.x}px`,
        }"
      >
        <p :class="$style.noMargin">
          {{ t('document.rubricate.signatureStamp.tooltipBody') }}
        </p>
        <template #activator>
          <button
            ref="signatureElements"
            :class="[
              isPageSigned(index) && $style.signed,
              page.kind === 'manuscript' && $style.manuscript,
              page.kind === 'initials' && [$style.initials, $style.rubric],
              $style.signature
            ]"
            :style="{
              cursor: !!artefact || isPageSigned(index) ? 'default' : '',
              transformOrigin: 'top left',
              transform: `scale(${scale})`,
            }"
            data-testid="signature"
            :tabindex="isPageSigned(index) ? -1 : 0"
            :data-page="page?.page"
            @click="signatureHandler(index)"
            @mousedown="(e) => togglePressedClass(e, $style.pressed)"
            @mouseup="(e) => togglePressedClass(e, $style.pressed)"
            @keydown="handleKeyDown($event, index)"
            @focus="stampSelectedIndex = index"
          >
            <template v-if="!isPageSigned(index)">
              <XIcon
                v-if="page.kind === 'manuscript'"
                :class="$style.icon"
                :icon="'signature'"
                left
                size="lg"
              />
              <span :class="$style.placeholder">
                {{
                  page.kind === 'manuscript'
                    ? $t('document.rubricate.signatureStamp.clickToSign')
                    : $t('document.rubricate.signatureStamp.clickToRubric')
                }}
              </span>
            </template>

            <span
              v-if="isPageSigned(index) && page.kind === 'manuscript'"
              :class="$style.signatureFinish"
              data-testid="manuscript"
            >
              <img
                v-if="artefactProperty.manuscript"
                :src="artefactProperty.manuscript"
                alt="signature"
              >
            </span>

            <span
              v-if="isPageSigned(index) && page.kind === 'initials'"
              data-testid="initials"
              :class="$style.signatureFinish"
            >
              {{ artefactProperty.initials }}
            </span>
          </button>
        </template>
      </component>
    </div>
  </Teleport>
</template>

<style lang="scss" module>
.signatures {
  width: 100%;

  .tooltip {
    position: absolute;
    z-index: var(--zindex-dropdown);
  }
}

.signature {
  --signature-width: 144px;
  --signature-height: 50px;
  --rubric-width: 72px;
  --rubric-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--signature-width);
  height: var(--signature-height);
  padding: var(--space-small-xxx) var(--space-small-xx);
  font-size: var(--font-size-small-x);
  font-weight: var(--font-weight-semibold);
  opacity: 0.9;
  color: var(--color-brand-primary-600);
  background: var(--color-brand-primary-50);
  border: var(--border-width-small) dashed var(--color-brand-primary-400);
  border-radius: var(--border-radius-medium);
  transition: background-color var(--time-transition-short);
  cursor: pointer;

  &.rubric {
    width: var(--rubric-width);
    height: var(--rubric-height);
  }

  &:hover {
    color: var(--color-neutral-0);
    background: var(--color-brand-primary-400);
    border-width: 0;
  }

  &:focus:not(.signed) {
    color: var(--color-neutral-0);
    background-color: var(--color-brand-primary-400);
    border-color: var(--color-neutral-0);
    border-style: solid;
    border-width: var(--border-width-large);
    box-shadow: 0px 0px 32px 0px var(--color-brand-primary-300);
  }

  &.pressed {
    color: var(--color-neutral-0);
    background-color: var(--color-brand-primary-600);
    border: none;

    .icon {
      background-color: var(--color-brand-primary-600);
    }
  }

  &.signed {
    padding: var(--space-small-xxx);
    color: var(--color-neutral-1000);
    background: var(--color-brand-primary-100);
    border-color: transparent;
    border-style: solid;
    border-width: var(--border-width-large);
    animation: borderAnimation var(--time-transition-long);

    .signatureFinish {
      display: flex;
      font-size: var(--font-size-large-xx);

      img {
        max-height: calc(var(--signature-height) - var(--border-width-large) - var(--space-small-xxx) * 2);
      }
    }

    &.manuscript {
      align-items: baseline;
      width: var(--signature-width);
    }

    &.initials {
      width: var(--rubric-width);
      font-family: 'Over the Rainbow', cursive;
    }
  }

  @keyframes borderAnimation {
    0% {
      border-color: var(--color-brand-primary-400);
    }

    100% {
      border-color: var(--color-brand-primary-200);
      border-width: var(--border-width-large);
    }
  }
}

.icon {
  padding: var(--space-small-xx);
  color: var(--color-neutral-0);
  background-color: var(--color-brand-primary-400);
  border-radius: 50%;
  transition: background-color var(--time-transition-short);
}

.placeholder {
  pointer-events: none;
}

.noMargin {
  margin: 0;
}
</style>
